/*
 * File: ProcedureFormTextFields.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 09/03/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Controls the text fields in the procedures form
 */
import React, { ChangeEventHandler } from "react";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { observer } from "mobx-react";
import { Grid, FormControl, Theme } from "@material-ui/core";
import { WithStyles } from "@material-ui/core";
import ChargeModel from "services/patients/post_charges/store/ChargeModel";
import { Instance } from "mobx-state-tree";

const styles = (theme: Theme) => ({
  margins: {
    margin: theme.spacing(1)
  },
  spacer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
});

interface Props extends WithStyles<typeof styles> {
  chargeModel: Instance<typeof ChargeModel>;
  allowModifiers: boolean | undefined;
  allowToothAndSurface: boolean | undefined;
}

const ProcedureFormTextField = ({
  chargeModel,
  allowModifiers,
  allowToothAndSurface,
  classes
}: Props) => {
  const disableUnitFields = chargeModel.calculationMethod === 0;
  const onChange: ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = e => {
    chargeModel.update(e.target.name as any, e.target.value);
  };

  /**
   *
   * Allows only the letters m, o, d, f, l, i, or b to be entered
   *  into the surface field.  These characters may only be entered once.
   * @param event: the surface text field changing input
   */
  const validateSurfaceCharacters: ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = event => {
    const fullArray = ["m", "o", "d", "f", "l", "i", "b"];
    const currentSurfaceCharacters = chargeModel.surface.split("");
    const remainingAllowedLetters = fullArray.filter(character => {
      return !currentSurfaceCharacters.includes(character);
    });
    if (remainingAllowedLetters.length > 0) {
      const validationRegex = new RegExp(
        remainingAllowedLetters.join("|"),
        "i"
      );
      if (
        (event.nativeEvent instanceof InputEvent &&
          event.nativeEvent.inputType === "deleteContentBackward") ||
        validationRegex.test(event.target.value)
      ) {
        chargeModel.update(event.target.name as any, event.target.value);
      }
    }
  };

  return (
    <div className={classes.margins}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            name="procedure"
            variant="filled"
            value={chargeModel.procedure}
            onChange={onChange}
            label="Procedure"
            disabled
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <FormControl fullWidth className={classes.spacer}>
            <TextField
              label="Description"
              variant="filled"
              value={chargeModel.description}
              onChange={onChange}
              disabled
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        {allowModifiers && (
          <Grid item xs={6} sm={3}>
            <TextField
              name="modifier1"
              value={chargeModel.modifier1}
              onChange={onChange}
              label="Modifier 1"
              variant="filled"
              fullWidth
            />
          </Grid>
        )}
        {allowModifiers && (
          <Grid item xs={6} sm={3}>
            <TextField
              name="modifier2"
              value={chargeModel.modifier2}
              onChange={onChange}
              label="Modifier 2"
              variant="filled"
              fullWidth
            />
          </Grid>
        )}
        {allowModifiers && (
          <Grid item xs={6} sm={3}>
            <TextField
              name="modifier3"
              value={chargeModel.modifier3}
              onChange={onChange}
              label="Modifier 3"
              variant="filled"
              fullWidth
            />
          </Grid>
        )}
        {allowModifiers && (
          <Grid item xs={6} sm={3}>
            <TextField
              name="modifier4"
              value={chargeModel.modifier4}
              onChange={onChange}
              label="Modifier 4"
              variant="filled"
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1} className={classes.spacer}>
        <Grid item xs={6}>
          <TextField
            disabled={disableUnitFields}
            name="units"
            value={chargeModel.units}
            onChange={onChange}
            label="Units"
            variant="filled"
            type="number"
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            disabled={true}
            type="number"
            name="unitFee"
            value={chargeModel.unitFee}
            onChange={onChange}
            label="Unit Fee"
            variant="filled"
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} className={classes.spacer}>
        {allowToothAndSurface && (
          <Grid item xs={6}>
            <TextField
              name="tooth"
              value={chargeModel.tooth ?? ""}
              onChange={onChange}
              label="Tooth"
              variant="filled"
              fullWidth
            />
          </Grid>
        )}
        {allowToothAndSurface && (
          <Grid item xs={6}>
            <TextField
              name="surface"
              value={chargeModel.surface}
              onChange={validateSurfaceCharacters}
              label="Surface"
              variant="filled"
              fullWidth
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default withStyles(styles)(observer(ProcedureFormTextField));
